import axios from 'axios';
import { LoginUser, LoginUserItem } from '@/utility/DTO/LoginUser';
import { toDateKey, toYm } from '@/utility/date';
import { bCodeLength, bCodeBaseLength } from '@/utility/consts/DbTables';
import { ListOption } from './ListOptions';
import { SssStaffOpt, SssBaseOpt } from '@/utility/SelectStaffSearch';
import { DeadlineSettingItem, DeadlineSetting } from '@/utility/deadline-setting';
import { DisplayError } from '@/error/error-ex';

export interface BasicItem {
  bases: BaseOptionItem[];
  loginUser: LoginUserItem;
  staffs: StaffOptionItem[];
  deadlines: DeadlineSettingItem[];
}

interface BaseOptionItem {
  bcode: string;
  bkname: string;
  office: boolean; //< Bjtype === 8
  btagno: number;
  sneedctgys: number[]; //< 必要な業務区分
  btocalc06: number; //< 保育補助員枠の許容基準、00＝不可、01＝子育て支援員なら可、02＝無資格者でも可
  needsCalcType: string;
  bstdate: string | Date;
  beddate?: string | Date;
}

interface StaffOptionItem {
  scode: number;
  sname: string;
  stagno: number;
  bcodes: string[];
}

export type BaseOption = BaseOptionItem;

export interface BasicData {
  baseOptions?: Record<string, BaseOption>; //< 配列に変更する zzz
  loginUser: LoginUser;
  sssStaffOpts: SssStaffOpt[];
  sssBaseOpts: SssBaseOpt[]; //< 全拠点名 暫定 computedに切替 zzz
  deadlines: DeadlineSetting[];
}

export const initBasicData: BasicData = {
  baseOptions: undefined,
  loginUser: {
    sEmployee: 0,
    sCategory: 0,
    sPosition: 0,
    sMainBase: '',
    sSubBase: '',
    bgCode: '',
    bgName: '',
    bgBCodes: [],
  },
  sssStaffOpts: [],
  sssBaseOpts: [],
  deadlines: [],
};

export interface BaseRange {
  bStdate: Date;
  bEddate: Date | undefined;
}

//  基本情報の取得 日付指定
export async function loadBasic(basic: BasicData, targetDate: Date, bcode: string, month: boolean): Promise<boolean> {
  const response = await axios.get<BasicItem>('/api/MasterBase/basic/' + toDateKey(targetDate), { params: { month: month } });
  if (!response) {
    return false;
  }

  //  mbeases
  const rec: Record<string, BaseOption> = {};
  response.data.bases.forEach(v => {
    const opt: BaseOption = {
      ...v,
      bstdate: (v.bstdate as string).dateTextToDate(),
      beddate: !v.beddate ? undefined : (v.beddate as string).dateTextToDate(),
    };
    rec[v.bcode] = opt;
  });
  basic.baseOptions = rec;

  if (
    !(bcode in basic.baseOptions) ||
    (!month && !isInBaseRangeDate(basic, bcode, targetDate)) ||
    (month && !isInBaseRangeMonth(basic, bcode, targetDate))
  ) {
    throw new DisplayError(`拠点が有効期限範囲内にありません ${bcode} ${toDateKey(targetDate)}`);
  }

  //  user情報
  basic.loginUser = { ...response.data.loginUser };

  //  SssStaffOpt
  basic.sssStaffOpts = response.data.staffs.map(v => {
    const opt: SssStaffOpt = {
      sCode: v.scode,
      sName: `${v.scode} ${v.sname}`,
      sTagNo: v.stagno,
      bCodes: [...v.bcodes],
    };
    return opt;
  });

  //  SssBaseOpt 暫定
  basic.sssBaseOpts = response.data.bases.map(v => {
    const opt: SssBaseOpt = {
      bCode: v.bcode,
      bNTiny: v.bkname,
      bTagNo: v.btagno,
    };
    return opt;
  });

  //  deadline
  basic.deadlines = [...response.data.deadlines];

  return true;
}

//  画面左上の拠点選択のリストを生成 従事者により異なる
export function getSelectableBaseOpts(basic: BasicData): ListOption<string>[] {
  if (!basic.baseOptions) return [];
  const isOffice = isOfficeUser(basic);

  const loginUser = basic.loginUser;

  //  拠点グループCode由来の拠点を追加 拠点グループ
  const bCodesByBgcode: string[] = loginUser.bgCode
    ? loginUser.bgCode.indexOf('Z') === 0
      ? [...loginUser.bgBCodes] //< 拠点グループ
      : [loginUser.bgCode] //< 一つの拠点
    : []; //< なし

  //  SMainBase,SSubBase由来の重複なしのbCode
  const set = new Set<string>();
  set.add(loginUser.sMainBase.slice(0, bCodeBaseLength));
  if (loginUser.sSubBase.length === bCodeLength) {
    set.add(loginUser.sSubBase.slice(0, bCodeBaseLength));
  }
  const baseList: string[] = [...set];

  //  拠点リストに登録
  const res: ListOption<string>[] = bCodesByBgcode.map(bCode => {
    const bopt: ListOption<string> = {
      value: basic.baseOptions?.[bCode].bcode ?? '',
      text: basic.baseOptions?.[bCode].bkname ?? '',
    };
    return bopt;
  });

  Object.keys(basic.baseOptions)
    .filter(bCode => isOffice || baseList.includes(bCode.slice(0, bCodeBaseLength)))
    .filter(bCode => !bCodesByBgcode.includes(bCode))
    .forEach(bCode => {
      const bopt: ListOption<string> = {
        value: basic.baseOptions?.[bCode].bcode ?? '',
        text: basic.baseOptions?.[bCode].bkname ?? '',
      };
      res.push(bopt);
    });

  return res;
}

//  --- 有効期限
//  日付を拠点有効期限内に収めるよう変換 undefined ... 拠点がない場合
export function toValidRange(basic: BasicData, bcode: string, date: Date): Date | undefined {
  // 前回で問題なし
  const range = getBaseRange(basic, bcode);
  if (!range) return undefined; //< どうにもならない

  if (range.bStdate.getTime() <= date.getTime() && (!range.bEddate || date.getTime() <= range.bEddate.getTime())) {
    return date;
  }

  const res = date.getTime() < range.bStdate.getTime() ? range.bStdate : range.bEddate ? range.bEddate : range.bStdate;
  return res;
}

// 拠点有効期限が存在するか
export function isExistBaseRange(basic: BasicData, bcode: string): boolean {
  const res = !!basic.baseOptions && bcode in basic.baseOptions;
  return res;
}

//  拠点有効期限の取得 ※存在が前提
export function getBaseRange(basic: BasicData, bcode: string): BaseRange | undefined {
  if (!basic.baseOptions || !(bcode in basic.baseOptions)) return undefined;
  const base = basic.baseOptions[bcode];
  const res: BaseRange = {
    bStdate: base.bstdate as Date,
    bEddate: base.beddate as Date,
  };
  return res;
}

// 日付が拠点有効期限内か ※有効範囲が存在ることが前提
function isInBaseRangeDate(basic: BasicData, bcode: string, date: Date): boolean {
  const range = getBaseRange(basic, bcode);
  if (!range) return false;
  const res = range.bStdate.getTime() <= date.getTime() && (!range.bEddate || date.getTime() <= range.bEddate.getTime());
  return res;
}

// その月は拠点有効期限内か ※有効範囲が存在ることが前提
export function isInBaseRangeMonth(basic: BasicData, bcode: string, date: Date): boolean {
  const range = getBaseRange(basic, bcode);
  if (!range) return false;
  const targetYm = toYm(date);
  const res =
    toYm(range.bStdate).getTime() <= targetYm.getTime() && (!range.bEddate || targetYm.getTime() <= toYm(range.bEddate).getTime());
  return res;
}

export function isInBaseRange(baseRange: BaseRange | undefined, date: Date): boolean {
  if (!baseRange) return false;
  const res = baseRange.bStdate.getTime() <= date.getTime() && (!baseRange.bEddate || date.getTime() <= baseRange.bEddate.getTime());
  return res;
}

//  ---
export function getBaseRecord(basic: BasicData): Record<string, string> {
  if (!basic.baseOptions) return {};

  const rec: Record<string, string> = {};
  Object.keys(basic.baseOptions).forEach(bCode => {
    if (basic.baseOptions) rec[bCode] = basic.baseOptions[bCode].bkname;
  });
  return rec;
}

export function isOfficeUser(basic: BasicData): boolean {
  if (!basic.baseOptions) return false;
  return (
    basic.baseOptions[basic.loginUser.sMainBase].office ||
    (basic.loginUser.sSubBase.length > 0 && basic.baseOptions[basic.loginUser.sSubBase].office)
  );
}

export function getBaseOpts(basic: BasicData): ListOption<string>[] {
  if (!basic.baseOptions) return [];

  const opts: ListOption<string>[] = Object.keys(basic.baseOptions)?.map(bCode => {
    const name = !basic.baseOptions ? '-' : basic.baseOptions[bCode as keyof ListOption<string>].bkname ?? '-';
    const res: ListOption<string> = {
      value: bCode,
      text: name,
    };
    return res;
  });
  return opts;
}

export function getCates(basic: BasicData, bCode: string): number[] | undefined {
  if (!basic.baseOptions) return undefined;
  return basic.baseOptions[bCode].sneedctgys.filter(v => v > 0);
}

export function getCategoryOpts(basic: BasicData, bCode: string, allCateOpts: ListOption<number>[]): ListOption<number>[] {
  if (!basic.baseOptions) return [];
  const cateIds = basic.baseOptions[bCode]?.sneedctgys ?? [0];
  return allCateOpts.filter(v => cateIds.some(x => x === v.value));
}

export function getExtendBaseOpts(
  allBaseOpts: ListOption<string>[],
  selectableBaseOpts: ListOption<string>[],
  bCode: string
): ListOption<string>[] {
  if (!bCode || selectableBaseOpts.find(v => v.value === bCode)) return selectableBaseOpts;
  const find = allBaseOpts.find(v => v.value === bCode);
  if (!find) return selectableBaseOpts;
  const opts = [...selectableBaseOpts];
  opts.push(find);
  return opts;
}
